import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Drawer from "@mui/material/Drawer";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Link from "next/link";
import { useRouter } from "next/router";

import { useI18n } from "@/i18n/I18nProvider";
import { arFont, enFont } from "@/styles/font";
import { outlinedButton } from "@/utils/styles/buttons";

import ServiceTile from "./ServiceTile";
import {
  AUDIENCE_TABS,
  DRAWER_ACCOUNT_ITEMS,
  DRAWER_COMPANY_ITEMS,
  DRAWER_INDIVIDUAL_ITEMS,
  DRAWER_PREFETCH_PATHS,
} from "./navbar.config";
import { useNavProducts } from "./navbar.hooks";
import { drawer, drawerAudienceTabState } from "./navbar.styles";

const NO_SECTIONS_OPEN = {};

/* Financing products are laid out three per row, with the last row centred. */
const COLUMNS_PER_ROW = 3;

function productColumnSpan(index, total) {
  const remainder = total % COLUMNS_PER_ROW;
  const lastRowStart = total - (remainder || COLUMNS_PER_ROW);

  if (index < lastRowStart) return 4;
  if (remainder === 1) return 12;
  if (remainder === 2) return 6;
  return 4;
}

export default function MobileDrawer({
  open,
  onClose,
  audience,
  isCompanyAudience,
  onSelectAudience,
  isLoggedIn,
  onLogout,
  allProduct,
}) {
  const { t, locale } = useI18n();
  const router = useRouter();

  /* Which expandable sections are currently open, keyed by section name. */
  const [openSections, setOpenSections] = useState(NO_SECTIONS_OPEN);

  const fontClass = locale === "en" ? enFont.className : arFont.className;

  useEffect(() => {
    if (!open) return;
    DRAWER_PREFETCH_PATHS.forEach((path) => router.prefetch(path));
  }, [open, router]);

  const toggleSection = (name) =>
    setOpenSections((previous) => ({ ...previous, [name]: !previous[name] }));

  /*
   * Picking a service collapses the sections as well as closing the drawer:
   * the destination is a different part of the site, so the expanded list the
   * visitor was browsing is no longer the right thing to come back to.
   */
  const leaveViaSection = () => {
    setOpenSections(NO_SECTIONS_OPEN);
    onClose();
  };

  const navigateTo = (href) => () => {
    router.push(href);
    onClose();
  };

  const openExternal = (href) => () => {
    window.open(href, "_blank", "noopener,noreferrer");
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    localStorage.removeItem("bro-to");
    onClose();
  };

  const items = isCompanyAudience
    ? DRAWER_COMPANY_ITEMS
    : DRAWER_INDIVIDUAL_ITEMS;

  const sectionContent = {
    financial: (
      <ProductsSection
        allProduct={allProduct}
        isCompanyAudience={isCompanyAudience}
        fontClass={fontClass}
        onNavigate={leaveViaSection}
      />
    ),
    insurance: (
      <TileSection
        tiles={t.secondary_nav.insurance_services_dropdown}
        fontClass={fontClass}
        onNavigate={leaveViaSection}
      />
    ),
    aboutContact: (
      <TileSection
        tiles={t.secondary_nav.about_contact_dropdown}
        fontClass={fontClass}
        onNavigate={leaveViaSection}
      />
    ),
    partnership: (
      <TileSection
        tiles={t.partner_dropdown}
        fontClass={fontClass}
        onNavigate={leaveViaSection}
      />
    ),
  };

  return (
    <Drawer
      anchor={locale === "en" ? "left" : "right"}
      open={open}
      onClose={onClose}
      dir={locale === "en" ? "ltr" : "rtl"}
      sx={{ zIndex: 1300 }}
      /*
       * PERF NOTE (measured, deliberately NOT "fixed" here):
       * opening this drawer is the app's worst interaction — ~1.2s on
       * throttled mobile, of which ~1.1s is presentation delay. The tap
       * handler only flips `open`; the cost is React mounting this whole
       * subtree plus ~40 emotion style insertions before the browser can
       * paint.
       *
       * ModalProps={{ keepMounted: true }} was tried and reverted: it moved
       * that work onto the load path (long tasks rose from ~5.7s to ~14s in
       * one run) and could not be shown to leave CLS — currently ~0 — intact
       * on this machine. See PERFORMANCE_OPTIMIZATION_REPORT.md.
       */
    >
      <Box
        role="presentation"
        sx={{
          ...drawer.panel,
          "& span": { textAlign: locale === "en" ? "left" : "right" },
        }}
      >
        {AUDIENCE_TABS.map(({ id, href, label }) => (
          <Box
            key={id}
            component={Link}
            href={href}
            onClick={() => onSelectAudience(id)}
            className={fontClass}
            sx={{
              ...drawer.audienceTab,
              ...drawerAudienceTabState(audience === id),
            }}
          >
            {label(t)}
          </Box>
        ))}

        <List>
          {items.map((item) => {
            const label = item.label(t);
            const icon = item.icon(locale);

            if (item.section) {
              const isOpen = Boolean(openSections[item.section]);

              return (
                <Box key={item.id}>
                  <DrawerItem
                    icon={icon}
                    label={label}
                    fontClass={fontClass}
                    onClick={() => toggleSection(item.section)}
                    adornment={isOpen ? <ExpandLess /> : <ExpandMore />}
                  />
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    {sectionContent[item.section]}
                  </Collapse>
                </Box>
              );
            }

            const href = item.href(locale);

            return (
              <DrawerItem
                key={item.id}
                icon={icon}
                label={label}
                fontClass={fontClass}
                onClick={
                  item.external ? openExternal(href) : navigateTo(href)
                }
              />
            );
          })}

          {!isCompanyAudience && (
            <Box>
              {isLoggedIn ? (
                <>
                  {DRAWER_ACCOUNT_ITEMS.map(({ id, href, icon, label }) => (
                    <DrawerItem
                      key={id}
                      icon={icon}
                      label={label(t)}
                      fontClass={fontClass}
                      onClick={navigateTo(href)}
                    />
                  ))}

                  <ListItem
                    disablePadding
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Button
                      variant="outlined"
                      aria-label="Logout button in mobile screen"
                      onClick={handleLogout}
                      className={fontClass}
                      sx={{ ...outlinedButton, mx: 2, mt: 2, width: "100%" }}
                    >
                      {t.userNav.logout}
                    </Button>
                  </ListItem>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    variant="outlined"
                    aria-label="Login button in mobile screen"
                    onClick={navigateTo("/login")}
                    className={fontClass}
                    sx={{ ...outlinedButton, mx: 2, mt: 2, width: "100%" }}
                  >
                    {t.secondary_nav.login}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </List>
      </Box>
    </Drawer>
  );
}

/* One row of the drawer: icon, label and an optional expand chevron. */
function DrawerItem({ icon, label, fontClass, onClick, adornment }) {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick}>
        <Box
          component="img"
          src={icon}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          width={20}
          height={20}
          /* Always sits on the side the label starts from. */
          sx={{ marginInlineEnd: "8px" }}
        />
        <ListItemText primary={label} className={fontClass} />
        {adornment}
      </ListItemButton>
    </ListItem>
  );
}

function TileSection({ tiles, fontClass, onNavigate }) {
  return (
    <Box sx={drawer.sectionContent} className={fontClass}>
      {tiles.map(({ title, img, url }) => (
        <ServiceTile
          key={url || title}
          url={url}
          title={title}
          img={img}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  );
}

function ProductsSection({
  allProduct,
  isCompanyAudience,
  fontClass,
  onNavigate,
}) {
  const products = useNavProducts(allProduct, { isCompanyAudience });

  return (
    <Grid container spacing={{ xs: 2 }} pt={2} className={fontClass}>
      {products.map(({ id, title, img, url }, index) => (
        <Grid
          key={id || url}
          item
          xs={productColumnSpan(index, products.length)}
          sx={{ mt: 0.5 }}
        >
          <ServiceTile
            url={url}
            title={title}
            img={img}
            onNavigate={onNavigate}
          />
        </Grid>
      ))}
    </Grid>
  );
}
